import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import BoxInput from '../BoxInput/BoxInput';
import Lottie from 'lottie-react';
import successLottie from '../../../utils/Lottie/NewSuccess.json';
import IconReusable from '../../../components/IconReusable';
import { toast } from 'react-toastify';

const OTPVerificationComponent = ({
  mobileNumber,
  onVerify,
  onResend,
  purpose = 'verification',
  otpLength = 4,
  initialTimer = 60,
  showMobileNumber = true,
  customMessage,
  onSuccess,
  onError
}) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [timer, setTimer] = useState(initialTimer);
  const [resendDisabled, setResendDisabled] = useState(true);

  useEffect(() => {
    if (timer > 0) {
      setResendDisabled(true);
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setResendDisabled(false);
    }
  }, [timer]);

  const handleResend = async () => {
    if (!resendDisabled && onResend) {
      setTimer(initialTimer);
      setResendDisabled(true);
      try {
        const result = await onResend();
        if (result?.success) {
          toast.success(result.message || 'OTP sent successfully');
        } else {
          toast.error(result?.message || 'Failed to send OTP');
        }
      } catch (error) {
        toast.error('Failed to send OTP');
      }
    }
  };

  const verifyOtp = async (otpValue) => {
    if (!onVerify) return;
    
    setLoading(true);
    setError('');
    setVerified(false);
    
    try {
      const result = await onVerify(otpValue);
      setLoading(false);
      
      if (result?.success) {
        setError('');
        setVerified(true);
        onSuccess?.(result);
      } else {
        setError(result?.message || 'Invalid OTP');
        setVerified(false);
        onError?.(result?.message || 'Invalid OTP');
      }
    } catch (error) {
      setLoading(false);
      setError('Verification failed. Please try again.');
      setVerified(false);
      onError?.('Verification failed. Please try again.');
    }
  };

  const handleOtpChange = (val) => {
    setOtp(val);
    if (val.length === otpLength) {
      verifyOtp(val);
    } else {
      setVerified(false);
      setError('');
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant='h2'>
        {customMessage || (showMobileNumber 
          ? `Please enter OTP sent to Mobile Number ${mobileNumber}`
          : 'Please enter the OTP'
        )}
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BoxInput
            length={otpLength}
            value={otp}
            onChange={handleOtpChange}
            type="text"
            showEyeIcon={false}
            error={!!error}
            disabled={loading}
          />
          {verified && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Lottie
                animationData={successLottie}
                loop={false}
                style={{ width: 24, height: 24 }}
              />
              <Typography variant='body1'>
                Verified
              </Typography>
            </Box>
          )}
        </Box>
        
        {error && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconReusable name="ErrorFilled" size={20} color="#CC0000" />
            <Typography variant='body1' sx={{ color: '#CC0000' }}>
              {error}
            </Typography>
          </Box>
        )}
      </Box>
      
      {onResend && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant='body1'>
            Didn't receive the OTP?
          </Typography>
          <Typography
            variant='labelD'
            component="span"
            sx={{
              color: resendDisabled ? '#D9D9D9' : '#995A00',
              cursor: resendDisabled ? 'not-allowed' : 'pointer'
            }}
            onClick={handleResend}
          >
            Resend
          </Typography>
          <Typography variant='body1'>
            OTP in {`00:${timer.toString().padStart(2, '0')}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OTPVerificationComponent;